import { createSelector } from '@reduxjs/toolkit';

import { InsightsState, InsightsStateSelector } from 'app-redux/InsightsState';
import * as dto from 'client/dto';
import { Geoname } from 'utils/constants';

import diseaseActivityPanelsSelectors from 'diseaseActivityPanels/diseaseActivityPanels.selectors';

import { LocationSelectionModel } from './components/LocationsModel';

const getAllLocationNamesById: InsightsStateSelector<Record<number, string>> = createSelector(
  state => state.locations.byId,
  byId =>
    Object.values(byId).reduce<Record<number, string>>((acc, item: dto.GetGeonameModel) => Object.assign(acc, { [item.geonameId]: item.name }), {})
);

const getLocationName = (locationId: number) => (state: InsightsState) => state.locations.byId[locationId]?.name;
const getLocationsSortBy = (state: InsightsState) => state.locations.sortBy;
const canDeleteLocations = (state: InsightsState) => state.locations.canDelete;
const getAllLocationIds = (state: InsightsState) => state.locations.allLocationIds;
const hasLocations = (state: InsightsState) => state.locations.allLocationIds.length > 0;
const isLoading = (state: InsightsState) => state.locations.isLoading;
const isEditing = (state: InsightsState) => state.locations.isEditing;
const hasError = (state: InsightsState) => state.locations.hasError;
const getLocation = (geonameId: number) => (state: InsightsState) => state.locations.byId[geonameId] ? state.locations.byId[geonameId] : {};
const getLocationIsDeleting = (geonameId: number) => (state: InsightsState) => state.locations.deleteInProgressById[geonameId];
const getAllLocations = (state: InsightsState) => Object.values(state?.locations?.byId ?? {});
const getAllLocationsForEditing = createSelector(
  getAllLocations,
  loc =>
    ({
      locations: loc.map(x => ({
        locationId: x.geonameId,
        name: x.name,
      })),
    } as LocationSelectionModel)
);
const getSelectedLocation = createSelector(
  diseaseActivityPanelsSelectors.getSelectedLocationId,
  state => state.locations.byId,
  (id, byId) => (id === Geoname.GLOBAL_VIEW ? null : byId[id])
);
const getSelectedLocationTitle = createSelector(
  diseaseActivityPanelsSelectors.getSelectedLocationId,
  state => state?.locations?.byId,
  (id, byId) => (id === Geoname.GLOBAL_VIEW ? 'Global View' : byId[id]?.fullDisplayName)
);
const getSelectedLocationTitleShort = createSelector(
  diseaseActivityPanelsSelectors.getSelectedLocationId,
  state => state.locations.byId,
  (id, byId) => (id === Geoname.GLOBAL_VIEW ? 'Global View' : byId[id]?.name)
);
const getSelectedLocationCountryName = createSelector(getSelectedLocation, loc => loc?.country);
const isSelectedLocationCountry = createSelector(getSelectedLocation, loc => loc?.locationType === dto.LocationType.Country);
const isUpdatingLocations = (state: InsightsState) => state.locations.isUpdateInProgress;
const isAddingLocation = (state: InsightsState) => state.locations.isAddInProgress;
const isSelectedLocationInvalid = createSelector(
  diseaseActivityPanelsSelectors.disacParams,
  state => state.locations.byId,
  (disacParams, byId) =>
    disacParams.isActive && disacParams.userLocationId != null && disacParams.userLocationId !== Geoname.GLOBAL_VIEW
      ? !byId[disacParams.userLocationId]
      : false
);

// locationsSelectors
export default {
  getAllLocationNamesById,
  getLocationName,
  getLocationsSortBy,
  canDeleteLocations,
  getAllLocationIds,
  hasLocations,
  isLoading,
  isEditing,
  hasError,
  getLocation,
  getLocationIsDeleting,
  getAllLocations,
  getAllLocationsForEditing,
  isUpdatingLocations,
  isAddingLocation,
  getSelectedLocation,
  getSelectedLocationTitle,
  getSelectedLocationTitleShort,
  getSelectedLocationCountryName,
  isSelectedLocationCountry,
  isSelectedLocationInvalid,
};
